import type { BotAction } from '../types';

/**
 * The `{{names}}` a call needs before it can be made.
 *
 * Read off the action's address and headers and the step's own request body, so a
 * test asks "what is mobile?" with one box rather than "paste the variables as
 * JSON" — which is the question that stopped people testing anything.
 */
export function placeholdersIn(...sources: unknown[]): string[] {
  const found = new Set<string>();
  const scan = (value: unknown) => {
    if (typeof value === 'string') {
      for (const m of value.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) found.add(m[1]!);
    } else if (Array.isArray(value)) {
      value.forEach(scan);
    } else if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(scan);
    }
  };
  sources.forEach(scan);
  return [...found];
}

export function actionConfig(action: BotAction): { method?: string; url?: string; headers?: Record<string, string> } {
  try {
    const parsed: unknown = JSON.parse(action.config);
    return parsed && typeof parsed === 'object' ? (parsed as { method?: string; url?: string; headers?: Record<string, string> }) : {};
  } catch {
    return {};
  }
}

/** A plausible value for a placeholder, so a test has something to send. */
export function sampleFor(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('mobile') || n.includes('phone')) return '9876543210';
  if (n.includes('email')) return 'someone@example.com';
  if (n.includes('name')) return 'Asha';
  if (n.includes('date')) return '01/09/2026';
  if (n.endsWith('_id') || n === 'id') return '1';
  return '';
}

/** A variable name for a picked path: `data.CustomerName` → `customer_name`. */
export function variableNameFor(path: string, taken: string[]): string {
  const last = path.split('.').filter((p) => !/^\d+$/.test(p)).pop() ?? 'value';
  let base = last.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'value';
  if (/^\d/.test(base)) base = `v_${base}`;
  let name = base;
  let n = 1;
  while (taken.includes(name)) name = `${base}_${++n}`;
  return name;
}
